
import React from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Learn from './pages/Learn';
import About from './pages/About';
import CSR from './pages/CSR';
import Contact from './pages/Contact';
import { LOGO_URL } from './constants';

const App: React.FC = () => {
  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-[#f0fdf4]">
        <Navbar />
        
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/csr" element={<CSR />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>

        <footer className="bg-emerald-950 text-emerald-100 py-24 px-6 mt-32 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500/0 via-grow-green to-emerald-500/0"></div>
          
          <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-16 relative z-10">
            {/* Left Section: Logo & Description */}
            <div className="md:col-span-5 space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-xl shadow-white/5 bg-white flex items-center justify-center p-1.5">
                  <img 
                    src={LOGO_URL} 
                    alt="Grow Local" 
                    className="w-full h-full object-contain scale-[2.8]"
                  />
                </div>
                <h3 className="text-3xl font-black serif-heading text-emerald-100">GrowLocal</h3>
              </div>
              <p className="text-xl text-emerald-200/50 max-w-sm font-medium leading-relaxed">
                Empowering the urban farming revolution through sustainable education.
              </p>
            </div>

            {/* Middle Section: Social Media & Email */}
            <div className="md:col-span-3 flex flex-col items-center md:items-start space-y-10">
              <div className="space-y-4 w-full">
                <h4 className="text-xs font-black uppercase tracking-widest text-emerald-100/50">Follow Us</h4>
                <div className="flex flex-col gap-4">
                  <a 
                    href="https://www.instagram.com/growlocal.ja/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="group flex items-center gap-4 hover:opacity-100 transition-opacity"
                    aria-label="Follow us on Instagram"
                  >
                    <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-emerald-100/70 group-hover:bg-grow-green group-hover:text-white transition-all shadow-lg border border-white/5">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-100/70 group-hover:text-white transition-colors">growlocal.ja</p>
                      <p className="text-[10px] uppercase font-black tracking-tighter opacity-30 group-hover:opacity-60 transition-opacity">Instagram</p>
                    </div>
                  </a>

                  <a 
                    href="https://www.tiktok.com/@ja_team25" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="group flex items-center gap-4 hover:opacity-100 transition-opacity"
                    aria-label="Follow us on TikTok"
                  >
                    <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-emerald-100/70 group-hover:bg-grow-green group-hover:text-white transition-all shadow-lg border border-white/5">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.03 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.9-.32-1.98-.23-2.81.33-.85.51-1.44 1.43-1.58 2.41-.14 1.02.23 2.1 1.01 2.79.64.55 1.42.82 2.25.82 1.18-.02 2.31-.62 2.91-1.63.31-.52.47-1.1.47-1.71V.02z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-100/70 group-hover:text-white transition-colors">growlocal.ja</p>
                      <p className="text-[10px] uppercase font-black tracking-tighter opacity-30 group-hover:opacity-60 transition-opacity">TikTok</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="space-y-4 w-full">
                <h4 className="text-xs font-black uppercase tracking-widest text-emerald-100/50">Direct Inquiry</h4>
                <a 
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=growlocal25@gmail.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="group flex items-center gap-4 hover:opacity-100 transition-opacity"
                  aria-label="Email us on Gmail"
                >
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-emerald-100/70 group-hover:bg-grow-green group-hover:text-white transition-all shadow-lg border border-white/5">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V8L12 13L20 8V18ZM12 11L4 6H20L12 11Z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-100/70 group-hover:text-white transition-colors">growlocal25@gmail.com</p>
                    <p className="text-[10px] uppercase font-black tracking-tighter opacity-30 group-hover:opacity-60 transition-opacity">Click to draft</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Section: JA Canada Info */}
            <div className="md:col-span-4 space-y-6">
              <h4 className="text-xs font-black uppercase tracking-widest text-emerald-100/50">JA Canada</h4>
              <div className="flex items-center gap-6">
                <p className="text-sm font-bold text-emerald-200/70 leading-snug flex-1">
                  Grow Local is a JA Central Ontario Company
                </p>
                <a 
                  href="https://www.jacentralontario.org/company-program/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-white rounded-xl flex-shrink-0 flex items-center justify-center p-2 shadow-lg hover:scale-105 transition-all group/ja"
                  title="Visit JA Central Ontario Website"
                >
                  <img 
                    src="https://images.squarespace-cdn.com/content/v1/620d6e97bbc44626186c910f/3e752b2a-9ebb-4a56-a665-2be558bfdc6a/JA+social+symbol.png" 
                    alt="JA Central Ontario Logo" 
                    className="w-full h-full object-contain"
                  />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
